import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faTriangleExclamation} from '@fortawesome/pro-solid-svg-icons';
import {faSpinnerThird} from '@fortawesome/pro-duotone-svg-icons';
import {
  BicBadgeComponent,
  BicButtonComponent,
  BicPopupComponent,
} from '@buildinginformationcloud/bic-ui-components';

import {RULESET_TYPE_CONFIG} from '../../../ruleManagement/models/rule-management.models';
import {AssignedRuleset} from '../../my-checks.service';

export interface DuplicateCheckSelection {
  includeRules: boolean;
  rulesetIds: string[];
}

type DuplicateMode = 'run-only' | 'run-and-rules';

/**
 * Rückfrage vor dem Duplizieren eines Prüflaufs aus dem BIC Katalog.
 *
 * <p>Ein BIC-Prüflauf zeigt auf zentral bereitgestellte, schreibgeschützte myChecks. Ein reines
 * Duplikat behält diese Bezüge – startbar, aber die Prüfregeln bleiben unveränderlich. Erst Kopien
 * im eigenen Projekt machen ihn anpassbar, und die will man selten alle.
 */
@Component({
  selector: 'app-duplicate-check-prompt',
  standalone: true,
  imports: [
    FormsModule,
    FaIconComponent,
    BicPopupComponent,
    BicButtonComponent,
    BicBadgeComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './duplicate-check-prompt.component.html',
})
export class DuplicateCheckPromptComponent implements OnChanges {
  @Input({required: true}) checkName!: string;
  @Input() rulesets: AssignedRuleset[] = [];
  @Input() loading = false;
  /** Zuordnung nicht lesbar – dann bleibt nur das Duplizieren des Prüflaufs selbst. */
  @Input() loadFailed = false;
  @Input() saving = false;

  @Output() closed = new EventEmitter<void>();
  @Output() confirmed = new EventEmitter<DuplicateCheckSelection>();

  protected mode: DuplicateMode = 'run-only';
  protected selected = new Set<string>();

  protected readonly faTriangleExclamation = faTriangleExclamation;
  protected readonly faSpinnerThird = faSpinnerThird;
  protected readonly typeConfig = RULESET_TYPE_CONFIG;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rulesets']) {
      this.selected = new Set(this.rulesets.map(entry => entry.ruleset.id));
    }
  }

  protected get hasRulesets(): boolean {
    return this.rulesets.length > 0;
  }

  protected get rulesOptionAvailable(): boolean {
    return this.hasRulesets && !this.loadFailed;
  }

  protected get allSelected(): boolean {
    return this.hasRulesets && this.selected.size === this.rulesets.length;
  }

  protected get confirmDisabled(): boolean {
    if (this.saving || this.loading) return true;
    return this.mode === 'run-and-rules' && this.selected.size === 0;
  }

  protected isSelected(id: string): boolean {
    return this.selected.has(id);
  }

  protected toggle(id: string): void {
    if (this.selected.has(id)) {
      this.selected.delete(id);
    } else {
      this.selected.add(id);
    }
  }

  protected toggleAll(): void {
    this.selected = this.allSelected ? new Set() : new Set(this.rulesets.map(e => e.ruleset.id));
  }

  protected chooseMode(mode: DuplicateMode): void {
    if (this.saving) return;
    if (mode === 'run-and-rules' && !this.rulesOptionAvailable) return;
    this.mode = mode;
  }

  protected onClose(): void {
    if (this.saving) return;
    this.closed.emit();
  }

  protected onConfirm(): void {
    if (this.confirmDisabled) return;
    const includeRules = this.mode === 'run-and-rules';
    this.confirmed.emit({includeRules, rulesetIds: includeRules ? [...this.selected] : []});
  }
}
